/**
 * Modul „CRM“: Kundenstamm (Schema `kunden`), API `/api/crm/v1/*`.
 * #815 Phase 1: Liste. #830: Vollschema (Person + Adressen, Kontaktkanäle, Einwilligungen, Quellen), Schreibpfade
 * (anlegen, bearbeiten, deaktivieren — nie löschen) und lückenloses Änderungsprotokoll (`kunden.version`, nur lesen).
 * Alle Aufzählungen entsprechen den CHECK-Constraints der Migration 0004 (Feldliste #830).
 */

/** Herkunft eines Kundensatzes (`kunden.person.quelle`) */
export const KUNDEN_QUELLEN = ['shopify', 'neo', 'portal'] as const;
export type KundenQuelle = (typeof KUNDEN_QUELLEN)[number];

export const KONTOSTATUS_SHOPIFY = ['ENABLED', 'INVITED', 'DISABLED', 'DECLINED'] as const;
export type KontostatusShopify = (typeof KONTOSTATUS_SHOPIFY)[number];
export const STATUS_NEO = ['aktiv', 'gesperrt'] as const;
export type StatusNeo = (typeof STATUS_NEO)[number];
export const ADRESSE_TYPEN = ['rechnung', 'liefer', 'standard'] as const;
export type AdresseTyp = (typeof ADRESSE_TYPEN)[number];
export const KONTAKT_KANAELE = ['email', 'telefon', 'mobil', 'fax'] as const;
export type KontaktKanal = (typeof KONTAKT_KANAELE)[number];
export const EINWILLIGUNG_KANAELE = ['email', 'sms', 'post', 'telefon', 'whatsapp'] as const;
export type EinwilligungKanal = (typeof EINWILLIGUNG_KANAELE)[number];
export const EINWILLIGUNG_STATUS = ['keine', 'einfach', 'bestaetigt', 'widerrufen'] as const;
export type EinwilligungStatus = (typeof EINWILLIGUNG_STATUS)[number];
export const QUELLE_SYSTEME = ['shopify', 'neo', 'loyalty', 'n8n_link', 'brevo', 'zoho', 'portal'] as const;
export type QuelleSystem = (typeof QUELLE_SYSTEME)[number];
export const VERSION_AKTIONEN = ['insert', 'update', 'delete', 'import', 'merge'] as const;
export type VersionAktion = (typeof VERSION_AKTIONEN)[number];
export const AKTEUR_TYPEN = ['mitarbeiter', 'agent', 'workflow', 'import', 'system'] as const;
export type AkteurTyp = (typeof AKTEUR_TYPEN)[number];
/** Tabellen in `kunden.*`, die protokolliert werden (Filter `?tabelle=`) */
export const VERSION_TABELLEN = ['person', 'adresse', 'kontaktkanal', 'einwilligung', 'quelle_ref', 'ereignis', 'abgleich', 'feld_quelle'] as const;
export type VersionTabelle = (typeof VERSION_TABELLEN)[number];

/** Eintrag der Kundenliste `GET /api/crm/v1/kunden` (nur aktive Personen, `deaktiviert_at IS NULL`) */
export interface CrmKunde {
  id: string;
  quelle: KundenQuelle;
  quellenId: string | null;
  name: string | null;
  email: string | null;
  telefon: string | null;
  /** ISO-8601 */
  erstelltAt: string;
  geaendertAt: string;
  /**
   * #839 „Kunde seit“ = `coalesce(erstellt_am_quelle, erstellt_at)` (Quellsystem, sonst Portal) — Basis von Filter kunde_seit_ab und KPI neu_*.
   * #851: NULL = unbekannt — NEO-Personen ohne Anlagedatum (NEO liefert keins; `erstellt_at` wäre nur das Importdatum).
   */
  kundeSeit: string | null;
  /** #846 Quellsysteme der Person (quelle_ref.system, sortiert) — Anzeige „Shop“, „NEO“, „Shop + NEO“ */
  quellenSysteme?: QuelleSystem[];
  /** #846 offener Abgleich mit Wirkung pruefen/verdacht (an der Person oder als Kandidat) → Badge „Abgleich prüfen“ */
  abgleichPruefen?: boolean;
}

/** #851 Filiale (kunden.filiale) — `GET /api/crm/v1/filialen`, Auswahl der Stammfiliale */
export interface CrmFiliale {
  kuerzel: string;
  name: string;
  aktiv: boolean;
}

/** Seitenweise Liste: `?seite=` (ab 1) und `?groesse=` (1–100), Standard 1/50; #839 Filter siehe `CrmKundenFilterQuery`, `gesamt` = gefilterte Anzahl */
export interface CrmKundenListe {
  eintraege: CrmKunde[];
  seite: number;
  groesse: number;
  gesamt: number;
}

/** Fachliche Felder der Person (#830) — alle optional/NULL-fähig; Schreibpfade POST/PATCH nehmen eine Teilmenge */
export interface CrmPersonFelder {
  anrede: string | null;
  titel: string | null;
  vorname: string | null;
  nachname: string | null;
  firma: string | null;
  geschlecht: string | null;
  /** YYYY-MM-DD */
  geburtstag: string | null;
  sprache: string | null;
  kundentyp: string | null;
  stammfiliale: string | null;
  kontostatusShopify: KontostatusShopify | null;
  statusNeo: StatusNeo | null;
  gesperrtGrund: string | null;
  blacklist: boolean;
  blacklistGrund: string | null;
  blacklistQuelle: string | null;
  blacklistAufgehobenAm: string | null;
  blacklistAufgehobenVon: string | null;
  tags: string[];
  kundengruppe: string | null;
  notiz: string | null;
  kassentext: string | null;
  erstelltAmQuelle: string | null;
  erstkauf: string | null;
  letztkauf: string | null;
  bestellanzahl: number | null;
  /** Dezimalzahl als String (numeric(12,2)) */
  umsatzGesamt: string | null;
  bonussaldo: string | null;
  bonuskarteNr: string | null;
  loyaltyStatus: string | null;
  dubletteVon: string | null;
}

export interface CrmAdresse {
  id: string;
  personId: string;
  typ: AdresseTyp | null;
  firma: string | null;
  strasse: string | null;
  hausnummer: string | null;
  zusatz: string | null;
  plz: string | null;
  ort: string | null;
  landRoh: string | null;
  landIso2: string | null;
  istStandard: boolean;
  quelle: string | null;
  gueltigAb: string | null;
  gueltigBis: string | null;
  erstelltAt: string;
  geaendertAt: string;
}

export interface CrmKontaktkanal {
  id: string;
  personId: string;
  kanal: KontaktKanal;
  wertRoh: string | null;
  /** serverseitig: E-Mail lowercase/trim, Telefon E.164 (Default DE) */
  wertNorm: string | null;
  istPrimaer: boolean;
  verifiziert: boolean;
  quelle: string | null;
  erstelltAt: string;
  geaendertAt: string;
}

export interface CrmEinwilligung {
  id: string;
  personId: string;
  kanal: EinwilligungKanal;
  status: EinwilligungStatus;
  doubleOptIn: boolean | null;
  zeitpunkt: string | null;
  quelleNachweis: string | null;
  widerrufAm: string | null;
  erstelltAt: string;
  geaendertAt: string;
}

export interface CrmQuelleRef {
  id: string;
  personId: string;
  system: QuelleSystem;
  fremdId: string;
  fremdNr2: string | null;
  zuletztGesehen: string | null;
  imQuellsystemGeloescht: boolean;
  /** #846 Kennzahlen je Quelle (NEO: kundeSaldo/details); Shopify-Kennzahlen stehen an der Person */
  umsatzGesamt?: string | null;
  umsatzStueck?: number | null;
  bonuspunkte?: string | null;
  erstkauf?: string | null;
  letztkauf?: string | null;
  statusQuelle?: string | null;
  filiale?: string | null;
}

export const ABGLEICH_WIRKUNGEN = ['zusammengefuehrt', 'pruefen', 'verdacht', 'haushalt'] as const; // #849 haushalt
export type AbgleichWirkung = (typeof ABGLEICH_WIRKUNGEN)[number];
export const ABGLEICH_STATUS = ['offen', 'bestaetigt', 'getrennt'] as const;
export type AbgleichStatus = (typeof ABGLEICH_STATUS)[number];

/** #846 Dubletten-Abgleich (kunden.abgleich) — begruendung ohne Klarwerte (Schlüsseltypen, Varianten, Person-IDs) */
export interface CrmAbgleich {
  id: string;
  personId: string;
  quelleRefId: string;
  kandidatPersonId: string | null;
  /** 1 NEO-Nr an Shopify · 2 E-Mail · 3 Telefon · 4 Name+PLZ+Straße · 5 Name+Geburtsdatum · 6 Fuzzy-Name+PLZ */
  stufe: number;
  wirkung: AbgleichWirkung;
  status: AbgleichStatus;
  begruendung: Record<string, unknown>;
  syncLaufId: string | null;
  erstelltAt: string;
}

/** #846 Feldwert je Quelle (kunden.feld_quelle); konflikt = Person hat abweichenden Wert */
export interface CrmFeldQuelle {
  id: string;
  quelleRefId: string;
  feld: string;
  wert: string | null;
  konflikt: boolean;
}

/** `GET /api/crm/v1/kunden/:id` — Person mit allen Unterressourcen */
export interface CrmPerson extends CrmKunde, CrmPersonFelder {
  deaktiviertAt: string | null;
  adressen: CrmAdresse[];
  kontaktkanaele: CrmKontaktkanal[];
  einwilligungen: CrmEinwilligung[];
  quellen: CrmQuelleRef[];
  /** #846 Abgleich-Einträge, an denen die Person beteiligt ist (als Person oder Kandidat) */
  abgleich?: CrmAbgleich[];
  /** #846 Feldwerte je Quelle (NEO) */
  feldQuellen?: CrmFeldQuelle[];
}

/** `POST /api/crm/v1/kunden` — Person anlegen (quelle Standard `portal`); `PATCH` nimmt dieselben Felder, alle optional */
export type CrmPersonAnlegen = Partial<CrmPersonFelder> & { quelle?: KundenQuelle; quellenId?: string | null; name?: string | null; kommentar?: string };
export type CrmPersonAendern = Partial<CrmPersonFelder> & { name?: string | null; kommentar?: string };
export interface CrmDeaktivieren {
  kommentar?: string;
}

export type CrmAdresseAnlegen = Partial<Omit<CrmAdresse, 'id' | 'personId' | 'erstelltAt' | 'geaendertAt'>> & { kommentar?: string };
export type CrmKontaktkanalAnlegen = Partial<Omit<CrmKontaktkanal, 'id' | 'personId' | 'wertNorm' | 'erstelltAt' | 'geaendertAt'>> & { kanal: KontaktKanal; wertRoh: string; kommentar?: string };
export type CrmKontaktkanalAendern = Partial<Omit<CrmKontaktkanal, 'id' | 'personId' | 'wertNorm' | 'erstelltAt' | 'geaendertAt'>> & { kommentar?: string };
export type CrmEinwilligungAnlegen = Partial<Omit<CrmEinwilligung, 'id' | 'personId' | 'erstelltAt' | 'geaendertAt'>> & { kanal: EinwilligungKanal; kommentar?: string };
export type CrmEinwilligungAendern = Partial<Omit<CrmEinwilligung, 'id' | 'personId' | 'kanal' | 'erstelltAt' | 'geaendertAt'>> & { kommentar?: string };

/** Eintrag des Änderungsprotokolls `kunden.version` (append-only; die API liest nur) */
export interface CrmVersion {
  id: number;
  tabelle: VersionTabelle;
  datensatzId: string;
  personId: string | null;
  aktion: VersionAktion;
  feld: string | null;
  wertAlt: unknown;
  wertNeu: unknown;
  geaendertVon: string;
  akteurTyp: AkteurTyp;
  quelleIp: string | null;
  requestId: string | null;
  zeitpunkt: string;
  kommentar: string | null;
}

/** `GET kunden/:id/versionen?feld=&akteur=&seite=` und `GET versionen?von=&bis=&akteur=&tabelle=&seite=&groesse=` */
export interface CrmVersionenListe {
  eintraege: CrmVersion[];
  seite: number;
  groesse: number;
  gesamt: number;
}

export const CRM_API = '/api/crm/v1';
export const CRM_SEITE_STANDARD = 1;
export const CRM_GROESSE_STANDARD = 50;
export const CRM_GROESSE_MAX = 100;

// #832 Shopify-Import (Script + n8n-Endpunkt `/api/crm/import/shopify*`, Bearer-Token, kein Session-Pfad)
export const SYNC_SYSTEME = ['shopify', 'neo'] as const;
export const SYNC_MODI = ['probe', 'full', 'delta', 'dry_run'] as const;
export const SYNC_QUELLEN = ['script', 'n8n'] as const;
export const SYNC_STATUS = ['laeuft', 'ok', 'fehler'] as const;
/** Obergrenze Nodes je `POST /api/crm/import/shopify` (= Seitengröße der Delta-Abfrage) */
export const CRM_IMPORT_MAX_JE_AUFRUF = 250;

/** `POST /api/crm/import/shopify/lauf` {modus:'delta'} */
export interface CrmImportLauf {
  lauf_id: string;
  /** cursor_bis des letzten OK-Laufs − 1 h; null = noch kein Lauf (Erstbefüllung per Script) */
  since: string | null;
  suche: string | null;
  query: string;
  felder: string;
  max_je_aufruf: number;
}

/** Zähler eines Upserts (`POST /api/crm/import/shopify`, Script-Ausgabe) */
export interface CrmImportZaehler {
  gelesen: number;
  neu: number;
  aktualisiert: number;
  unveraendert: number;
  fehler: number;
  lauf_id: string;
}

/**
 * #839 Listenfilter `GET /api/crm/v1/kunden` (alle optional, UND-verknüpft; ungültig → 400 `crm.eingabe.<name>`).
 * Dieselben SQL-Bedingungen zählen die KPI-Leiste (`GET /api/crm/v1/kpi`) — Kachelwert = `gesamt` der gefilterten Liste.
 */
export const CRM_EINWILLIGUNG_FILTER = ['gueltig', 'bestaetigt', 'einfach', 'widerrufen', 'keine'] as const;
export type CrmEinwilligungFilter = (typeof CRM_EINWILLIGUNG_FILTER)[number];
export const CRM_FILTER_PARAMETER = ['status', 'einwilligung', 'blacklist', 'ohne_email', 'kunde_seit_ab', 'letztkauf_ab', 'umsatz_min'] as const;
export type CrmFilterParameter = (typeof CRM_FILTER_PARAMETER)[number];
export interface CrmKundenFilterQuery {
  /** kontostatus_shopify */
  status?: KontostatusShopify;
  /** E-Mail-Einwilligung: gueltig = bestaetigt|einfach; keine = kein E-Mail-Eintrag oder Status keine */
  einwilligung?: CrmEinwilligungFilter;
  blacklist?: 'true' | 'false';
  /** keine aktive E-Mail: person.email leer UND kein Kontaktkanal email mit wert_norm */
  ohne_email?: 'true';
  /** ISO-Datum JJJJ-MM-TT, ab 00:00 Europe/Berlin, auf „Kunde seit“ */
  kunde_seit_ab?: string;
  /** ISO-Datum JJJJ-MM-TT, ab 00:00 Europe/Berlin, auf letztkauf */
  letztkauf_ab?: string;
  /** umsatz_gesamt ≥ Zahl (EUR, Punkt als Dezimaltrenner) */
  umsatz_min?: string;
}

export type CrmAmpel = 'rot' | 'gelb' | 'gruen';
/** Letzter Abgleich (jüngster `kunden.sync_lauf` ohne dry_run) */
export interface CrmLetzterAbgleich {
  system: (typeof SYNC_SYSTEME)[number];
  gestartetAt: string;
  beendetAt: string | null;
  modus: (typeof SYNC_MODI)[number];
  status: (typeof SYNC_STATUS)[number];
  neu: number;
  aktualisiert: number;
  /** rot: status fehler oder gestartet vor mehr als 36 h; gelb: laeuft; sonst gruen */
  ampel: CrmAmpel;
}
/** Grenzen der Zeitkennzahlen (ISO-Datum, Europe/Berlin) — die UI verlinkt genau diese Werte als Filter */
export interface CrmKpiGrenzen {
  neu_30t: string;
  neu_12m: string;
  letztkauf_12m: string;
}
/**
 * #839 KPI-Leiste `GET /api/crm/v1/kpi` — nur Zählwerte, keine Personendaten. Basis: aktive Personen
 * (`deaktiviert_at IS NULL`, wie die Liste). Jeder Wert entspricht einem Listenfilter (`CrmKundenFilterQuery`).
 */
export interface CrmKpi {
  personen: number;
  aktiv_shopify: number;
  neu_30t: number;
  neu_12m: number;
  mit_umsatz: number;
  letztkauf_12m: number;
  einwilligung_gueltig: number;
  einwilligung_bestaetigt: number;
  einwilligung_einfach: number;
  einwilligung_widerrufen: number;
  blacklist: number;
  ohne_email: number;
  grenzen: CrmKpiGrenzen;
  letzter_abgleich: CrmLetzterAbgleich | null;
  /** Zeitpunkt der Berechnung (ISO-8601) */
  stand: string;
}
/** Abgleich gilt nach dieser Zeit als veraltet (Ampel rot) */
export const CRM_ABGLEICH_MAX_STUNDEN = 36;
